import React, { useState, useMemo } from 'react';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import {
  TrendUp,
  TrendDown,
  Minus,
  Users,
  Warning,
  PencilSimple,
  Trash,
  MagnifyingGlass,
  Heartbeat,
} from '@phosphor-icons/react';

const ClientTable = ({
  clients,
  canWrite,
  selectedClientIds,
  setSelectedClientIds,
  onBulkDeleteOpen,
  onEditClient,
  onDeleteClient,
  onClientClick,
  totalClients,
  totalPages,
  serverPage,
  onPageChange,
  onSearchChange,
  onSortChange,
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [sortOption, setSortOption] = useState('name_asc');
  const currentPage = serverPage || 1;
  const ITEMS_PER_PAGE = 50;

  // Debounce search to avoid firing on every keystroke
  const searchTimerRef = React.useRef(null);
  const handleSearch = (val) => {
    setSearchQuery(val);
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    searchTimerRef.current = setTimeout(() => {
      if (onSearchChange) onSearchChange(val);
    }, 400);
  };

  const handleSort = (val) => {
    setSortOption(val);
    if (onSortChange) onSortChange(val);
  };

  const paginatedClients = clients;
  const total = totalClients || clients.length;
  const pages = totalPages || Math.ceil(total / ITEMS_PER_PAGE);

  const handleSelectClient = (clientId) => {
    setSelectedClientIds(prev =>
      prev.includes(clientId)
        ? prev.filter(id => id !== clientId)
        : [...prev, clientId]
    );
  };

  const handleSelectAll = () => {
    const visibleIds = paginatedClients.map(c => c.id);
    const allSelected = visibleIds.every(id => selectedClientIds.includes(id));
    if (allSelected) {
      setSelectedClientIds(prev => prev.filter(id => !visibleIds.includes(id)));
    } else {
      setSelectedClientIds(prev => [...new Set([...prev, ...visibleIds])]);
    }
  };

  const formatMonthLabel = (label) => {
    if (!label) return '—';
    const [year, month] = label.split('-');
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return `${months[parseInt(month) - 1]} ${year}`;
  };

  const latestMonthLabel = clients.find(c => c.latest_month_label)?.latest_month_label;
  const prevMonthLabel = clients.find(c => c.prev_month_label)?.prev_month_label;

  return (
    <div className="bg-card border border-border rounded-sm p-4 sm:p-6">
      <div className="flex items-center justify-between mb-3 sm:mb-4">
        <div className="flex items-center gap-2 sm:gap-4 min-w-0">
          <div className="text-base sm:text-xl font-semibold tracking-tight truncate">Client Overview</div>
          {selectedClientIds.length > 0 && canWrite && (
            <Button
              onClick={onBulkDeleteOpen}
              variant="destructive"
              size="sm"
              className="rounded-sm text-xs sm:text-sm shrink-0"
              data-testid="bulk-delete-button"
            >
              <Trash size={14} weight="regular" className="mr-1 sm:mr-2" />
              <span className="hidden sm:inline">Delete </span>{selectedClientIds.length}
            </Button>
          )}
        </div>
        <span className="text-xs sm:text-sm text-muted-foreground shrink-0 ml-2">{total} clients</span>
      </div>

      <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 mb-4 sm:mb-6">
        <div className="relative flex-1 min-w-0">
          <MagnifyingGlass size={16} weight="regular" className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={searchQuery}
            onChange={(e) => handleSearch(e.target.value)}
            placeholder="Search clients..."
            className="pl-9 rounded-sm"
            data-testid="client-search-input"
          />
        </div>
        <div className="flex items-center gap-2">
          <Select value={sortOption} onValueChange={handleSort}>
            <SelectTrigger className="w-full sm:w-[220px] rounded-sm" data-testid="sort-by-select">
              <SelectValue placeholder="Sort by" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="name_asc">Name (A - Z)</SelectItem>
              <SelectItem value="name_desc">Name (Z - A)</SelectItem>
              <SelectItem value="created_desc">Newest First</SelectItem>
              <SelectItem value="created_asc">Oldest First</SelectItem>
            </SelectContent>
          </Select>
          {(searchQuery || sortOption !== 'name_asc') && (
            <Button
              variant="ghost"
              size="sm"
              className="rounded-sm text-xs shrink-0"
              onClick={() => { handleSearch(''); handleSort('name_asc'); }}
              data-testid="clear-search-sort"
            >
              Clear
            </Button>
          )}
        </div>
      </div>
      {paginatedClients.length === 0 ? (
        <div className="text-center py-12 text-muted-foreground">
          <Users size={48} weight="thin" className="mx-auto mb-4 opacity-50" />
          <p>No clients found. Add your first client to get started.</p>
        </div>
      ) : (
        <div className="overflow-x-auto -mx-4 sm:mx-0">
          <table className="w-full min-w-[600px]">
            <thead>
              <tr className="border-b border-border">
                {canWrite && (
                <th className="text-center py-2 sm:py-3 px-2 sm:px-4 w-10 sm:w-12">
                  <Checkbox
                    checked={paginatedClients.length > 0 && paginatedClients.every(c => selectedClientIds.includes(c.id))}
                    onCheckedChange={handleSelectAll}
                    data-testid="select-all-checkbox"
                  />
                </th>
                )}
                <th className="text-left py-2 sm:py-3 px-2 sm:px-4 text-[10px] sm:text-xs tracking-[0.2em] uppercase font-bold text-muted-foreground">Client</th>
                <th className="text-right py-2 sm:py-3 px-2 sm:px-4 text-[10px] sm:text-xs tracking-[0.2em] uppercase font-bold text-muted-foreground">Total</th>
                {prevMonthLabel && (
                  <th className="text-right py-2 sm:py-3 px-2 sm:px-4 text-[10px] sm:text-xs tracking-[0.2em] uppercase font-bold text-muted-foreground hidden lg:table-cell">{formatMonthLabel(prevMonthLabel)}</th>
                )}
                {latestMonthLabel && (
                  <th className="text-right py-2 sm:py-3 px-2 sm:px-4 text-[10px] sm:text-xs tracking-[0.2em] uppercase font-bold text-muted-foreground hidden md:table-cell">{formatMonthLabel(latestMonthLabel)}</th>
                )}
                <th className="text-center py-2 sm:py-3 px-2 sm:px-4 text-[10px] sm:text-xs tracking-[0.2em] uppercase font-bold text-muted-foreground hidden sm:table-cell">Growth</th>
                <th className="text-center py-2 sm:py-3 px-2 sm:px-4 text-[10px] sm:text-xs tracking-[0.2em] uppercase font-bold text-muted-foreground hidden sm:table-cell">Trend</th>
                <th className="text-center py-2 sm:py-3 px-2 sm:px-4 text-[10px] sm:text-xs tracking-[0.2em] uppercase font-bold text-muted-foreground">Status</th>
                <th className="text-center py-2 sm:py-3 px-2 sm:px-4 text-[10px] sm:text-xs tracking-[0.2em] uppercase font-bold text-muted-foreground hidden md:table-cell">Health</th>
                {canWrite && (
                <th className="text-center py-2 sm:py-3 px-2 sm:px-4 text-[10px] sm:text-xs tracking-[0.2em] uppercase font-bold text-muted-foreground">Actions</th>
                )}
              </tr>
            </thead>
            <tbody>
              {paginatedClients.map((client) => (
                <tr
                  key={client.id}
                  data-testid={`client-row-${client.id}`}
                  className={`client-row border-b border-border ${client.is_dipping ? 'dip-alert-row' : ''}`}
                >
                  {canWrite && (
                  <td className="py-3 sm:py-4 px-2 sm:px-4 text-center">
                    <Checkbox
                      checked={selectedClientIds.includes(client.id)}
                      onCheckedChange={() => handleSelectClient(client.id)}
                      data-testid={`select-client-${client.id}`}
                    />
                  </td>
                  )}
                  <td className="py-3 sm:py-4 px-2 sm:px-4">
                    <div className="font-medium text-sm cursor-pointer hover:text-accent transition-colors" onClick={() => onClientClick && onClientClick(client.id)} data-testid={`client-name-${client.id}`}>{client.name}</div>
                    {client.company && <div className="text-xs text-muted-foreground">{client.company}</div>}
                  </td>
                  <td className="py-3 sm:py-4 px-2 sm:px-4 text-right font-semibold text-sm">
                    ₹{client.total_sales.toLocaleString()}
                  </td>
                  {prevMonthLabel && (
                    <td className="py-3 sm:py-4 px-2 sm:px-4 text-right text-muted-foreground text-sm hidden lg:table-cell" data-testid={`prev-month-${client.id}`}>
                      {client.prev_month_total != null ? `₹${client.prev_month_total.toLocaleString()}` : '—'}
                    </td>
                  )}
                  {latestMonthLabel && (
                    <td className="py-3 sm:py-4 px-2 sm:px-4 text-right hidden md:table-cell" data-testid={`latest-month-${client.id}`}>
                      {client.latest_month_total != null ? (
                        <span className="font-semibold text-sm">₹{client.latest_month_total.toLocaleString()}</span>
                      ) : '—'}
                    </td>
                  )}
                  <td className="py-3 sm:py-4 px-2 sm:px-4 text-center hidden sm:table-cell">
                    {client.growth_percentage !== null && client.growth_percentage !== undefined ? (
                      <span className={`font-semibold text-sm ${client.growth_percentage > 0 ? 'text-accent' : client.growth_percentage < 0 ? 'text-destructive' : 'text-muted-foreground'}`}>
                        {client.growth_percentage > 0 ? '+' : ''}{client.growth_percentage}%
                      </span>
                    ) : (
                      <span className="text-muted-foreground text-xs">—</span>
                    )}
                  </td>
                  <td className="py-3 sm:py-4 px-2 sm:px-4 text-center hidden sm:table-cell">
                    {client.sales_trend === 'up' && <TrendUp size={18} weight="bold" className="inline text-accent" />}
                    {client.sales_trend === 'down' && <TrendDown size={18} weight="bold" className="inline text-destructive" />}
                    {client.sales_trend === 'stable' && <Minus size={18} weight="bold" className="inline text-muted-foreground" />}
                  </td>
                  <td className="py-3 sm:py-4 px-2 sm:px-4 text-center">
                    {client.is_dipping ? (
                      <span
                        data-testid={`dip-badge-${client.id}`}
                        className="inline-flex items-center px-2 sm:px-3 py-0.5 sm:py-1 rounded-sm bg-destructive text-destructive-foreground text-[10px] sm:text-xs font-bold tracking-wide"
                      >
                        <Warning size={12} weight="bold" className="mr-0.5 sm:mr-1" />
                        ALERT
                      </span>
                    ) : (
                      <span className="text-muted-foreground text-[10px] sm:text-xs tracking-wide">OK</span>
                    )}
                  </td>
                  <td className="py-3 sm:py-4 px-2 sm:px-4 text-center hidden md:table-cell">
                    {client.health_label ? (
                      <span
                        data-testid={`health-badge-${client.id}`}
                        className={`inline-flex items-center gap-1 px-2 py-0.5 sm:py-1 rounded-sm text-[10px] sm:text-xs font-bold tracking-wide ${
                          client.health_label === 'Healthy'
                            ? 'bg-green-500/15 text-green-600 dark:text-green-400'
                            : client.health_label === 'At Risk'
                            ? 'bg-yellow-500/15 text-yellow-600 dark:text-yellow-400'
                            : 'bg-red-500/15 text-red-600 dark:text-red-400'
                        }`}
                        title={`RFM Score: ${client.health_score}`}
                      >
                        <Heartbeat size={12} weight="bold" />
                        {client.health_label === 'Healthy' ? 'GOOD' : client.health_label === 'At Risk' ? 'RISK' : 'LOW'}
                      </span>
                    ) : (
                      <span className="text-muted-foreground text-xs">—</span>
                    )}
                  </td>
                  {canWrite && (
                  <td className="py-3 sm:py-4 px-2 sm:px-4 text-center">
                    <div className="flex items-center justify-center gap-1">
                      <button
                        onClick={() => onEditClient(client)}
                        className="p-1.5 sm:p-2 hover:bg-muted rounded-sm transition-colors"
                        title="Edit client"
                        data-testid={`edit-client-${client.id}`}
                      >
                        <PencilSimple size={16} weight="regular" />
                      </button>
                      <button
                        onClick={() => onDeleteClient(client)}
                        className="p-1.5 sm:p-2 hover:bg-destructive/10 text-destructive rounded-sm transition-colors"
                        title="Delete client"
                        data-testid={`delete-client-${client.id}`}
                      >
                        <Trash size={16} weight="regular" />
                      </button>
                    </div>
                  </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {pages > 1 && (
        <div className="flex flex-col sm:flex-row items-center justify-between gap-3 mt-4 pt-4 border-t border-border">
          <span className="text-xs sm:text-sm text-muted-foreground">
            {((currentPage - 1) * ITEMS_PER_PAGE) + 1}–{Math.min(currentPage * ITEMS_PER_PAGE, total)} of {total}
          </span>
          <div className="flex items-center gap-1">
            <Button
              variant="outline"
              size="sm"
              className="rounded-sm h-8 px-2 sm:px-3 text-xs"
              disabled={currentPage === 1}
              onClick={() => onPageChange(1)}
              data-testid="page-first"
            >
              First
            </Button>
            <Button
              variant="outline"
              size="sm"
              className="rounded-sm h-8 w-8 p-0"
              disabled={currentPage === 1}
              onClick={() => onPageChange(currentPage - 1)}
              data-testid="page-prev"
            >
              &lt;
            </Button>
            {Array.from({ length: Math.min(3, pages) }, (_, i) => {
              let page;
              if (pages <= 3) {
                page = i + 1;
              } else if (currentPage <= 2) {
                page = i + 1;
              } else if (currentPage >= pages - 1) {
                page = pages - 2 + i;
              } else {
                page = currentPage - 1 + i;
              }
              return (
                <Button
                  key={page}
                  variant={page === currentPage ? 'default' : 'outline'}
                  size="sm"
                  className="rounded-sm h-8 w-8 p-0"
                  onClick={() => onPageChange(page)}
                  data-testid={`page-${page}`}
                >
                  {page}
                </Button>
              );
            })}
            <Button
              variant="outline"
              size="sm"
              className="rounded-sm h-8 w-8 p-0"
              disabled={currentPage === pages}
              onClick={() => onPageChange(currentPage + 1)}
              data-testid="page-next"
            >
              &gt;
            </Button>
            <Button
              variant="outline"
              size="sm"
              className="rounded-sm h-8 px-2 sm:px-3 text-xs"
              disabled={currentPage === pages}
              onClick={() => onPageChange(pages)}
              data-testid="page-last"
            >
              Last
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export const ClientDialogs = ({
  editClientOpen,
  setEditClientOpen,
  deleteClientOpen,
  setDeleteClientOpen,
  bulkDeleteOpen,
  setBulkDeleteOpen,
  selectedClient,
  setSelectedClient,
  selectedClientIds,
  onEditSubmit,
  onDeleteConfirm,
  onBulkDeleteConfirm,
}) => (
  <>
    <Dialog open={editClientOpen} onOpenChange={setEditClientOpen}>
      <DialogContent className="rounded-sm">
        <DialogHeader>
          <DialogTitle className="text-2xl tracking-tight">Edit Client</DialogTitle>
        </DialogHeader>
        {selectedClient && (
          <form onSubmit={onEditSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label className="text-xs tracking-[0.2em] uppercase font-bold text-muted-foreground">Client Name *</Label>
              <Input value={selectedClient.name} onChange={(e) => setSelectedClient({ ...selectedClient, name: e.target.value })} className="rounded-sm" required />
            </div>
            <div className="space-y-2">
              <Label className="text-xs tracking-[0.2em] uppercase font-bold text-muted-foreground">Email</Label>
              <Input type="email" value={selectedClient.email || ''} onChange={(e) => setSelectedClient({ ...selectedClient, email: e.target.value })} className="rounded-sm" />
            </div>
            <div className="space-y-2">
              <Label className="text-xs tracking-[0.2em] uppercase font-bold text-muted-foreground">Company</Label>
              <Input value={selectedClient.company || ''} onChange={(e) => setSelectedClient({ ...selectedClient, company: e.target.value })} className="rounded-sm" />
            </div>
            <div className="space-y-2">
              <Label className="text-xs tracking-[0.2em] uppercase font-bold text-muted-foreground">Industry</Label>
              <Input value={selectedClient.industry || ''} onChange={(e) => setSelectedClient({ ...selectedClient, industry: e.target.value })} className="rounded-sm" />
            </div>
            <div className="space-y-2">
              <Label className="text-xs tracking-[0.2em] uppercase font-bold text-muted-foreground">Region</Label>
              <Input value={selectedClient.region || ''} onChange={(e) => setSelectedClient({ ...selectedClient, region: e.target.value })} className="rounded-sm" />
            </div>
            <Button type="submit" className="w-full rounded-sm">Update Client</Button>
          </form>
        )}
      </DialogContent>
    </Dialog>

    <Dialog open={deleteClientOpen} onOpenChange={setDeleteClientOpen}>
      <DialogContent className="rounded-sm">
        <DialogHeader>
          <DialogTitle className="text-2xl tracking-tight">Delete Client</DialogTitle>
        </DialogHeader>
        {selectedClient && (
          <div className="space-y-4">
            <p className="text-muted-foreground">
              Are you sure you want to delete <strong>{selectedClient.name}</strong>?
              This will also delete all associated sales transactions. This action cannot be undone.
            </p>
            <div className="flex gap-3">
              <Button variant="outline" onClick={() => setDeleteClientOpen(false)} className="flex-1 rounded-sm">Cancel</Button>
              <Button variant="destructive" onClick={onDeleteConfirm} className="flex-1 rounded-sm">Delete Client</Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>

    <Dialog open={bulkDeleteOpen} onOpenChange={setBulkDeleteOpen}>
      <DialogContent className="rounded-sm">
        <DialogHeader>
          <DialogTitle className="text-2xl tracking-tight">Delete Multiple Clients</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <p className="text-muted-foreground">
            Are you sure you want to delete <strong>{selectedClientIds.length} clients</strong>?
            This will also delete all associated sales transactions. This action cannot be undone.
          </p>
          <div className="flex gap-3">
            <Button variant="outline" onClick={() => setBulkDeleteOpen(false)} className="flex-1 rounded-sm">Cancel</Button>
            <Button variant="destructive" onClick={onBulkDeleteConfirm} className="flex-1 rounded-sm" data-testid="confirm-bulk-delete">
              Delete {selectedClientIds.length} Clients
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  </>
);

export default ClientTable;
